import React, { useState } from 'react';
import { X, ImageUp, Plus, RefreshCw, TriangleAlert, Sparkles } from 'lucide-react';
import { extractProductInfoAI, mapProductFieldsAI } from './aiExtract';
import './PopulateValuesModal.css';

const MAX_SHOTS        = 4;
const MAX_USER_RETRIES = 2;

/* ── Small crystal gem icon for the header ── */
function CrystalIcon({ size = 22, uid = '0' }) {
  const h = Math.round(size * 1.5);
  return (
    <svg
      className="pvm-brand-icon"
      width={size}
      height={h}
      viewBox="0 0 80 120"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={`ci-tl-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#f0e6ff" />
          <stop offset="100%" stopColor="#c4b5fd" />
        </linearGradient>
        <linearGradient id={`ci-tr-${uid}`} x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
        <linearGradient id={`ci-l-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#7c3aed" />
          <stop offset="100%" stopColor="#4c1d95" />
        </linearGradient>
        <linearGradient id={`ci-b-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#6d28d9" />
          <stop offset="100%" stopColor="#2e1065" />
        </linearGradient>
      </defs>
      {/* 6 triangular facets all converging at center point (40,52) */}
      <polygon points="40,4 10,34 40,52"   fill={`url(#ci-tl-${uid})`} />
      <polygon points="40,4 70,34 40,52"   fill={`url(#ci-tr-${uid})`} />
      <polygon points="10,34 14,82 40,52"  fill={`url(#ci-tr-${uid})`} opacity="0.8" />
      <polygon points="70,34 66,82 40,52"  fill={`url(#ci-l-${uid})`} />
      <polygon points="14,82 40,116 40,52" fill={`url(#ci-l-${uid})`} opacity="0.9" />
      <polygon points="66,82 40,116 40,52" fill={`url(#ci-b-${uid})`} />
      {/* catch-light highlights simulate a light source from the upper-left */}
      <polygon points="40,4 58,22 40,52"  fill="white" opacity="0.55" />
      <polygon points="40,4 10,34 40,52"  fill="white" opacity="0.22" />
      {/* girdle outline + internal facet lines */}
      <polyline points="10,34 14,82 40,116 66,82 70,34 40,4 10,34" fill="none" stroke="rgba(139,92,246,0.4)" strokeWidth="0.8" />
      <line x1="40" y1="4"  x2="40" y2="52" stroke="rgba(139,92,246,0.25)" strokeWidth="0.7" />
      <line x1="10" y1="34" x2="40" y2="52" stroke="rgba(139,92,246,0.25)" strokeWidth="0.7" />
      <line x1="70" y1="34" x2="40" y2="52" stroke="rgba(139,92,246,0.25)" strokeWidth="0.7" />
      <line x1="14" y1="82" x2="40" y2="52" stroke="rgba(139,92,246,0.25)" strokeWidth="0.7" />
      <line x1="66" y1="82" x2="40" y2="52" stroke="rgba(139,92,246,0.25)" strokeWidth="0.7" />
    </svg>
  );
}

/* ── Large animated crystal gem rendered in the modal body ── */
function CrystalGem({ state = 'idle' }) {
  return (
    <div className={`crystal-wrap crystal-${state}`}>
      {/* perspective container — children share the same 3D space */}
      <div className="crystal-scene">
        <svg
          className="crystal-body"
          width="80"
          height="120"
          viewBox="0 0 80 120"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="cg-tl" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#f5eeff" />
              <stop offset="100%" stopColor="#c4b5fd" />
            </linearGradient>
            <linearGradient id="cg-tr" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%"   stopColor="#ddd6fe" />
              <stop offset="100%" stopColor="#a78bfa" />
            </linearGradient>
            <linearGradient id="cg-l" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#a78bfa" />
              <stop offset="100%" stopColor="#6d28d9" />
            </linearGradient>
            <linearGradient id="cg-r" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%"   stopColor="#c4b5fd" />
              <stop offset="100%" stopColor="#7c3aed" />
            </linearGradient>
            <linearGradient id="cg-bl" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#7c3aed" />
              <stop offset="100%" stopColor="#3b0764" />
            </linearGradient>
            <linearGradient id="cg-br" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%"   stopColor="#6d28d9" />
              <stop offset="100%" stopColor="#2e1065" />
            </linearGradient>
          </defs>
          <polygon points="40,4 10,34 40,52"   fill="url(#cg-tl)" />
          <polygon points="40,4 70,34 40,52"   fill="url(#cg-tr)" />
          <polygon points="10,34 14,82 40,52"  fill="url(#cg-l)" />
          <polygon points="70,34 66,82 40,52"  fill="url(#cg-r)" />
          <polygon points="14,82 40,116 40,52" fill="url(#cg-bl)" />
          <polygon points="66,82 40,116 40,52" fill="url(#cg-br)" />
          {/* catch-light: broad fill + narrow specular spike */}
          <polygon points="40,4 10,34 40,52"  fill="white" opacity="0.22" />
          <polygon points="40,4 58,22 40,52"  fill="white" opacity="0.58" />
          {/* girdle + facet lines */}
          <polyline points="10,34 14,82 40,116 66,82 70,34 40,4 10,34" fill="none" stroke="rgba(167,139,250,0.5)" strokeWidth="1" />
          <line x1="40" y1="4"  x2="40" y2="52" stroke="rgba(167,139,250,0.3)" strokeWidth="0.8" />
          <line x1="10" y1="34" x2="40" y2="52" stroke="rgba(167,139,250,0.3)" strokeWidth="0.8" />
          <line x1="70" y1="34" x2="40" y2="52" stroke="rgba(167,139,250,0.3)" strokeWidth="0.8" />
          <line x1="14" y1="82" x2="40" y2="52" stroke="rgba(167,139,250,0.3)" strokeWidth="0.8" />
          <line x1="66" y1="82" x2="40" y2="52" stroke="rgba(167,139,250,0.3)" strokeWidth="0.8" />
        </svg>

        {/* Orbit rings + sparkle particles — only mounted while AI is running */}
        {state === 'active' && (
          <>
            {/* Two tilted rings orbiting the crystal using rotateX to fake a 3D plane */}
            <div className="crystal-orbit crystal-orbit-1" />
            <div className="crystal-orbit crystal-orbit-2" />
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className="crystal-sparkle"
                style={{
                  '--spk-delay': `${(i * 0.37).toFixed(2)}s`,
                  '--spk-angle': `${i * 60}deg`,
                  '--spk-dist':  `${50 + (i % 3) * 10}px`,
                }}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Main modal component
   Props:
     onClose       — called when user dismisses the modal
     onApply(result) — called with { name, specs, mappedFields }
     targetFields  — [{ key, hint }] for Stage 2 field mapping,
                     or null/empty for free-form callers (AddComponent)
──────────────────────────────────────────────────────────────── */
export default function PopulateValuesModal({ onClose, onApply, targetFields }) {
  const [shots, setShots]             = useState([]);   // [{ file, preview }]
  const [isRunning, setIsRunning]     = useState(false);
  const [isFailed, setIsFailed]       = useState(false);
  const [statusMsg, setStatusMsg]     = useState('');
  const [error, setError]             = useState('');
  const [userRetries, setUserRetries] = useState(0);

  const retriesLeft  = MAX_USER_RETRIES - userRetries;
  const exhausted    = isFailed && retriesLeft <= 0;
  const crystalState = isRunning ? 'active' : isFailed ? 'failed' : 'idle';

  const addShots = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setShots(prev =>
      [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))].slice(0, MAX_SHOTS)
    );
    setError('');
    e.target.value = '';
  };

  const removeShot = (i) => setShots(prev => prev.filter((_, idx) => idx !== i));

  const runExtraction = async () => {
    if (!shots.length) return;
    let failed = false;
    setIsRunning(true);
    setIsFailed(false);
    setError('');

    try {
      /* Stage 1 — vision model reads the screenshots.
         The Cloud Function itself retries up to 3 times server-side with
         escalating prompts before giving up, so a single call here covers
         the internal retry budget. */
      setStatusMsg('Crystal AI reading your screenshots…');
      const extracted = await extractProductInfoAI(shots.map(s => s.file));

      // Cloud Function returns { name, specs[] } — no success flag.
      // Treat both fields empty as a failure.
      if (!extracted.name && (!Array.isArray(extracted.specs) || extracted.specs.length === 0)) {
        throw new Error("Crystal AI couldn't find any product information in these screenshots. Try a clearer image.");
      }

      /* Stage 2 — a second, independent NVIDIA model cross-checks the
         extraction and maps it onto the caller's exact form fields.
         This is best-effort: if it fails, the caller's own ruleMap fills gaps. */
      let mappedFields = null;
      if (Array.isArray(targetFields) && targetFields.length > 0) {
        setStatusMsg('Verifying with second AI model…');
        try {
          const verified = await mapProductFieldsAI(extracted.name, extracted.specs, targetFields);
          if (verified && verified.fields) mappedFields = verified.fields;
        } catch { /* non-fatal — caller falls back to rule-based mapping */ }
      }

      setStatusMsg('Done!');
      /* Keep the crystal spinning for a beat so the user sees the "Done!" state,
         then hand the result to the parent and let it unmount this modal. */
      setTimeout(() => {
        onApply({ name: extracted.name, specs: extracted.specs, mappedFields });
        onClose();
      }, 420);
      return; // skip finally's setIsRunning(false) — modal closes naturally

    } catch (err) {
      failed = true;
      setIsFailed(true);
      setError(err.message || 'Extraction failed. Try a clearer screenshot.');
    } finally {
      if (failed) setIsRunning(false);
    }
  };

  const handleRetry = () => {
    setUserRetries(prev => prev + 1);
    runExtraction();
  };

  return (
    <div className="pvm-overlay" onClick={onClose}>
      <div className="pvm-dialog" onClick={e => e.stopPropagation()}>

        {/* ── Header: Crystal AI branding ── */}
        <div className="pvm-header">
          <div className="pvm-brand">
            <CrystalIcon size={22} uid="h" />
            <div className="pvm-brand-text">
              <h3 className="pvm-brand-name">Crystal AI</h3>
              <p className="pvm-brand-sub">Powered by NVIDIA</p>
            </div>
          </div>
          <button className="pvm-close" onClick={onClose} disabled={isRunning} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* ── Running state: gem spins + progress bar ── */}
        {isRunning && (
          <div className="pvm-crystal-stage">
            <CrystalGem state="active" />
            <p className="pvm-stage-status">{statusMsg}</p>
            <p className="pvm-stage-substatus">
              Analyzing {shots.length} screenshot{shots.length !== 1 ? 's' : ''}
            </p>
            <div className="pvm-bar-track">
              <div className="pvm-bar-indeterminate" />
            </div>
            <div className="pvm-stage-dots">
              {[0, 1, 2].map(i => (
                <span key={i} className="pvm-dot" style={{ '--dot-delay': `${i * 0.22}s` }} />
              ))}
            </div>
          </div>
        )}

        {/* ── Idle / failed state: gem floats + upload zone + actions ── */}
        {!isRunning && (
          <div className="pvm-idle-layout">
            <div className="pvm-gem-area">
              <CrystalGem state={crystalState} />
            </div>

            {/* Retry budget banner */}
            {isFailed && !exhausted && (
              <div className="pvm-alert pvm-alert-error">
                <TriangleAlert size={13} />
                <span>
                  {error} — {retriesLeft} retr{retriesLeft === 1 ? 'y' : 'ies'} left
                </span>
              </div>
            )}

            {/* Exhausted: tell user to change the screenshot */}
            {exhausted && (
              <>
                <div className="pvm-alert pvm-alert-error">
                  <TriangleAlert size={13} />
                  <span>{error}</span>
                </div>
                <div className="pvm-alert pvm-alert-warn">
                  <TriangleAlert size={13} />
                  <span>
                    Crystal AI couldn't read these screenshots. Try uploading a clearer photo that
                    shows the full product title and spec table without ads or navigation in the way.
                  </span>
                </div>
              </>
            )}

            {/* Screenshot upload area */}
            {shots.length === 0 ? (
              <label className="pvm-dropzone">
                <input type="file" accept="image/*" multiple onChange={addShots} hidden />
                <ImageUp size={20} className="pvm-dropzone-icon" />
                <span className="pvm-dropzone-label">Upload screenshots</span>
                <span className="pvm-dropzone-hint">Up to {MAX_SHOTS} — one per page works best</span>
              </label>
            ) : (
              <>
                <div className="pvm-shot-grid">
                  {shots.map((s, i) => (
                    <div className="pvm-shot-thumb" key={i}>
                      <img src={s.preview} alt={`screenshot ${i + 1}`} />
                      <button type="button" className="pvm-shot-remove" onClick={() => removeShot(i)}>✕</button>
                    </div>
                  ))}
                  {shots.length < MAX_SHOTS && (
                    <label className="pvm-shot-thumb pvm-shot-add">
                      <input type="file" accept="image/*" multiple onChange={addShots} hidden />
                      <Plus size={15} />
                    </label>
                  )}
                </div>
                <p className="pvm-shot-count">{shots.length} of {MAX_SHOTS} screenshots selected</p>
              </>
            )}

            {/* Primary action + cancel */}
            <div className="pvm-actions">
              {!isFailed && (
                <button
                  type="button"
                  className="ap-btn ap-btn-ai pvm-action-btn"
                  disabled={!shots.length}
                  onClick={runExtraction}
                >
                  <Sparkles size={13} /> Extract with Crystal AI
                </button>
              )}
              {isFailed && !exhausted && (
                <button type="button" className="ap-btn ap-btn-ai pvm-action-btn" onClick={handleRetry}>
                  <RefreshCw size={13} /> Retry
                </button>
              )}
              {exhausted
                ? <button type="button" className="ap-btn ap-btn-ghost" onClick={onClose}>Close</button>
                : <button type="button" className="ap-btn ap-btn-ghost" onClick={onClose}>Cancel</button>
              }
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
